// Only explicitly opted-in editorial videos belong to this controller.
// Workshop carousels, product media and showcase dialogs retain their own owners.
export class DeferredVideo {
  constructor(video, environment = window) {
    this.video = video;
    this.viewport = video.closest?.('[data-deferred-video-viewport]') || video;
    this.environment = environment;
    this.document = video.ownerDocument;
    this.active = true;
    this.visible = false;
    this.cleanups = [];
    this.originalControls = video.controls;
    this.originalAriaHidden = video.getAttribute('aria-hidden');
    this.motion = environment.matchMedia?.('(prefers-reduced-motion: reduce)');
    this.connection = environment.navigator?.connection;
    video.autoplay = false;
    video.setAttribute('preload', 'none');

    this.listen(video, 'pause', () => {
      if (this.expectedPause) { this.expectedPause = false; return; }
      this.manualPlayback = false;
      if (this.active && this.visible && !this.document.hidden) this.userPaused = true;
    });
    this.listen(video, 'play', () => {
      if (!this.pendingPlay) {
        this.manualPlayback = true;
        this.manualControls = true;
        this.userPaused = false;
        this.blocked = false;
        this.updateControls();
      }
      if (!this.active || !this.visible || this.document.hidden || (!this.autoplayAllowed() && !this.manualPlayback)) this.stop();
    });
    this.listen(this.document, 'visibilitychange', () => this.sync());
    const preferenceChange = () => { this.stop(); this.updateControls(); this.sync(); };
    if (this.motion?.addEventListener) this.listen(this.motion, 'change', preferenceChange);
    else if (this.motion?.addListener) {
      this.motion.addListener(preferenceChange);
      this.cleanups.push(() => this.motion.removeListener(preferenceChange));
    }
    this.listen(this.connection, 'change', preferenceChange);
    this.updateControls();

    if (environment.IntersectionObserver) {
      this.nearObserver = new environment.IntersectionObserver(entries => {
        if (!this.active || !entries.some(entry => entry.isIntersecting)) return;
        this.hydrate();
        this.nearObserver.disconnect();
      }, { rootMargin: '400px 0px' });
      this.visibilityObserver = new environment.IntersectionObserver(entries => {
        if (!this.active) return;
        const entry = entries[entries.length - 1];
        this.visible = entry.isIntersecting && entry.intersectionRatio >= 0.15;
        this.sync();
      }, { threshold: [0, 0.15] });
      this.nearObserver.observe(this.viewport);
      this.visibilityObserver.observe(this.viewport);
    } else {
      // Legacy browsers retain native playback without starting offscreen films.
      this.hydrate();
      video.controls = true;
      video.removeAttribute('aria-hidden');
      const updateVisibility = () => {
        const box = this.viewport.getBoundingClientRect();
        this.visible = box.bottom > 0 && box.top < environment.innerHeight && box.right > 0 && box.left < environment.innerWidth;
        if (!this.visible || this.document.hidden) this.stop();
      };
      this.listen(environment, 'scroll', updateVisibility, { passive: true, capture: true });
      this.listen(environment, 'resize', updateVisibility, { passive: true });
      updateVisibility();
    }
  }

  listen(target, event, callback, options) {
    if (!target?.addEventListener) return;
    target.addEventListener(event, callback, options);
    this.cleanups.push(() => target.removeEventListener(event, callback, options));
  }

  autoplayAllowed() { return !this.motion?.matches && !this.connection?.saveData; }

  updateControls() {
    this.video.controls = this.originalControls || !this.autoplayAllowed() || Boolean(this.blocked) || Boolean(this.manualControls);
    if (this.video.controls) this.video.removeAttribute('aria-hidden');
    else if (this.originalAriaHidden !== null) this.video.setAttribute('aria-hidden', this.originalAriaHidden);
  }

  hydrate() {
    if (this.hydrated || !this.active) return;
    this.hydrated = true;
    const poster = this.video.getAttribute('data-poster');
    if (poster) this.video.setAttribute('poster', poster);
    const source = this.video.getAttribute('data-src');
    if (source) this.video.setAttribute('src', source);
    this.video.querySelectorAll('source[data-src]').forEach(item => item.setAttribute('src', item.getAttribute('data-src')));
    // preload=none enables native controls without fetching the film on Save-Data.
    this.video.load();
  }

  sync() {
    if (!this.active) return;
    if (!this.visible || this.document.hidden) { this.stop(); return; }
    this.hydrate();
    if (!this.autoplayAllowed()) {
      if (!this.manualPlayback) this.stop();
      return;
    }
    if (this.manualPlayback || this.userPaused || this.blocked || !this.video.paused || this.pendingPlay) return;
    const request = {};
    this.pendingPlay = request;
    const failed = () => {
      if (this.pendingPlay !== request) return;
      this.pendingPlay = null;
      if (request.cancelled) { this.sync(); return; }
      if (this.active && this.visible && !this.document.hidden && this.autoplayAllowed() && !this.userPaused) {
        this.blocked = true;
        this.updateControls();
      }
    };
    try {
      Promise.resolve(this.video.play()).then(() => {
        if (this.pendingPlay === request) this.pendingPlay = null;
        // A play promise can succeed after scrolling, a preference change or unload.
        if (!this.active || !this.visible || this.document.hidden || this.userPaused || (!this.autoplayAllowed() && !this.manualPlayback)) this.pause();
      }, failed);
    } catch (_) { failed(); }
  }

  pause() {
    if (this.pendingPlay) this.pendingPlay.cancelled = true;
    if (this.video.paused) return;
    this.expectedPause = true;
    this.video.pause();
  }

  stop() { this.manualPlayback = false; this.pause(); }

  destroy() {
    if (!this.active) return;
    this.active = false;
    this.nearObserver?.disconnect();
    this.visibilityObserver?.disconnect();
    this.cleanups.forEach(cleanup => cleanup());
    this.cleanups = [];
    this.stop();
  }
}

const controllers = new Map();

export function mountDeferredVideos(root = document) {
  const videos = [...root.querySelectorAll('video[data-deferred-video]')];
  if (root.matches?.('video[data-deferred-video]')) videos.unshift(root);
  videos.forEach(video => {
    if (!controllers.has(video)) controllers.set(video, new DeferredVideo(video));
  });
}

if (typeof document !== 'undefined') {
  mountDeferredVideos();
  document.addEventListener('shopify:section:load', event => mountDeferredVideos(event.target));
  document.addEventListener('shopify:section:unload', event => {
    controllers.forEach((controller, video) => {
      if (!event.target.contains(video)) return;
      controller.destroy();
      controllers.delete(video);
    });
  });
}
